<template>
  <div class="search-filter-bar">
    <div class="field has-addons">
      <div class="control has-icons-left is-rounded is-flex-grow-1">
        <span class="icon is-left">
          <i class="fas fa-search"></i>
        </span>
        <input
          class="input is-rounded"
          type="search"
          :placeholder="placeholder"
          :value="store.q"
          style="z-index: 3"
          inputmode="search"
          @input="debouncedSearch($event.target.value)"
        />
      </div>

      <div class="control">
        <button
          title="Advanced Filtering"
          class="button rounded-right"
          :class="{ 'is-active': showAdvanced, 'is-info': showAdvanced }"
          @click="showAdvanced = !showAdvanced"
        >
          <span class="icon is-left">
            <i class="fa-light fa-sliders-h"></i>
          </span>
          <span>Filters</span>
        </button>
      </div>
    </div>

    <div v-if="!showAdvanced" class="filter-summary" @click="showAdvanced = true">
      <span class="filter-summary-text">{{ summary }}</span>
      <a v-if="store.hasActiveFilters" class="reset-link" @click.stop="store.resetSearch">
        Clear Filters
      </a>
    </div>

    <div v-show="showAdvanced" class="box">
      <slot />

      <div class="field mt-3">
        <a @click.prevent="store.resetSearch">
          <span class="icon-text">
            <span class="icon has-text-danger">
              <i class="fa fa-times"></i>
            </span>
            <span class="is-bold">Clear filters</span>
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>import { ref as _ref } from 'vue';

  import { debounce } from "lodash-es";

  /*
   * The chrome around a search list's filters: the search box, the Filters toggle, the
   * one-line summary shown while the panel is closed, and the clear links. The fields
   * themselves go in the default slot, which renders inside the advanced panel.
   *
   * Expects a store built by create-search-list-store that declares a `q` string filter;
   * typing writes to it, debounced, and each keystroke that lands reloads the list.
   *
   * `summary` is the caller's job because only it can name its own filters. Whether the
   * clear links show is not -- that comes from the store, so a filter added to the
   * descriptor list is counted without touching this component.
   */
  const SEARCH_DEBOUNCE_MS = 300;

  const props = defineProps({
    store: { type: Object, required: true },
    summary: { type: String, default: "" },
    placeholder: { type: String, default: "Search..." },
  });

  let showAdvanced = _ref(false);

  const debouncedSearch = debounce((q) => props.store.setFilter("q", q), SEARCH_DEBOUNCE_MS);
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";

  .box {
    margin-bottom: 1.5rem;
  }

  .filter-summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0.5rem;
    margin-bottom: 0.5rem;
    border-radius: $radius;
    cursor: pointer;
    font-size: 0.875rem;

    &:hover {
      background-color: $white-bis;
    }

    .reset-link {
      white-space: nowrap;
      color: $grey;
      flex-shrink: 0;
      margin-right: 0.5rem;
    }
  }
</style>
